import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Table } from 'src/design/molecules/Table';
import React, { useId } from 'react';
import { useUserChatMemoryQuery } from 'src/api/queries/chat/useChatMemoryQueries';
import { useDeleteUserChatMemory } from 'src/api/queries/chat/useChatMemoryMutations';
import {
  memoryKindLabel,
  memorySourceLabel,
  memoryStatusLabel,
} from 'src/app/chat/memoryNotes';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { formatDate, formatNumber } from 'src/utils';

export interface IUserMemorySectionProps {
  userId: number;
}

/** A user's F.I.R.E. memory notes for an admin: read-only apart from Delete. */
export const UserMemorySection: React.FC<IUserMemorySectionProps> = ({
  userId,
}) => {
  const titleId = useId();
  const { data, isLoading } = useUserChatMemoryQuery(userId);
  const deleteNote = useDeleteUserChatMemory();
  const notes = data?.notes ?? [];

  return (
    <Paper p="md" withBorder component="section" aria-labelledby={titleId}>
      <Stack gap="md">
        <Group gap="sm" align="center">
          <Title order={4} id={titleId}>
            Memory
          </Title>
          {data && (
            <Badge variant="light" color={data.enabled ? 'green' : 'gray'}>
              {data.enabled ? 'Memory on' : 'Memory off'}
            </Badge>
          )}
        </Group>
        {isLoading && <Loader size="sm" />}
        {data && notes.length === 0 && (
          <Text size="sm" c="bright">
            This user has no memory notes.
          </Text>
        )}
        {notes.length > 0 && (
          <Table.ScrollContainer minWidth={720}>
            <Table aria-labelledby={titleId} fz="sm" verticalSpacing={4}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kind</Table.Th>
                  <Table.Th>Note</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Last used</Table.Th>
                  <Table.Th ta="right">Uses</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {notes.map((note) => (
                  <Table.Tr key={note.id}>
                    <Table.Td>{memoryKindLabel(note.kind)}</Table.Td>
                    <Table.Td style={{ overflowWrap: 'anywhere' }}>
                      {note.text}
                    </Table.Td>
                    <Table.Td>{memoryStatusLabel(note.status)}</Table.Td>
                    <Table.Td>{memorySourceLabel(note.source)}</Table.Td>
                    <Table.Td>{formatDate(note.createdAt)}</Table.Td>
                    <Table.Td>
                      {note.lastUsedAt ? formatDate(note.lastUsedAt) : 'Never'}
                    </Table.Td>
                    <Table.Td ta="right">
                      {formatNumber(note.useCount)}
                    </Table.Td>
                    <Table.Td>
                      <DeleteActionIcon
                        size="sm"
                        variant="subtle"
                        label="Delete note"
                        confirmationTitle="Delete this note?"
                        confirmationText="The note is removed from this user's memory. This cannot be undone."
                        onDelete={async () => {
                          await deleteNote.mutateAsync({ userId, id: note.id });
                        }}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
};
